import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "../layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { AdminInvoiceStats, AdminUpdateInvoice } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const AdminInvoices = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const { data: invoiceResponse, isLoading, error } = useQuery({
    queryKey: ["adminInvoiceStats"],
    queryFn: AdminInvoiceStats,
  });

  const allInvoices = invoiceResponse?.results || [];

  // Derive unique payment methods for filter dropdown
  const paymentMethods = useMemo(() => {
    const methods = [
      ...new Set(
        allInvoices
          .map((inv) => inv.payment_method)
          .filter(Boolean)
      ),
    ];
    return methods;
  }, [allInvoices]);

  const filteredInvoices = useMemo(() => {
    let result = [...allInvoices];

    if (statusFilter !== "all") {
      result = result.filter(
        (inv) => inv.status?.toLowerCase() === statusFilter
      );
    }

    if (methodFilter !== "all") {
      result = result.filter(
        (inv) => inv.payment_method?.toLowerCase() === methodFilter
      );
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter((inv) => new Date(inv.created_at) >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter((inv) => new Date(inv.created_at) <= to);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.reference?.toLowerCase().includes(term) ||
          inv.customer_meta?.patient_first_name?.toLowerCase().includes(term) ||
          inv.customer_meta?.patient_last_name?.toLowerCase().includes(term) ||
          inv.customer_meta?.patient_hospital_number?.includes(term) ||
          inv.amount?.includes(term)
      );
    }

    return result;
  }, [allInvoices, searchTerm, statusFilter, methodFilter, dateFrom, dateTo]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => AdminUpdateInvoice(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminInvoiceStats"] });
      setEditInvoice(null);
      setNewStatus("");
      alert("Invoice status updated successfully");
    },
    onError: (err) => {
      alert("Failed to update status: " + (err.message || "Unknown error"));
    },
  });

  const handleSaveStatus = () => {
    if (!newStatus || newStatus === editInvoice?.status) {
      setEditInvoice(null);
      return;
    }
    updateStatusMutation.mutate({ id: editInvoice.id, status: newStatus });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setMethodFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const amountColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return "text-green-600 font-semibold";
    if (s === "failed") return "text-red-500 font-semibold";
    return "text-orange-500 font-semibold";
  };

  const statusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === "completed" || s === "success" || s === "paid")
      return "bg-green-100 text-green-700";
    if (s === "pending") return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  if (user.role !== "ADMIN") {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl text-red-600">
        Access Denied
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Invoice Transactions</h1>
        <p className="text-gray-500 mt-1">
          View all patient invoice transaction records
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-5 mb-6 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Search by name, reference, hospital number, amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
          <Button variant="outline" onClick={clearFilters} className="shrink-0">
            Clear Filters
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          {/* Status */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Method */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">
              Payment Method
            </label>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                {paymentMethods.map((method) => (
                  <SelectItem key={method} value={method.toLowerCase()}>
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date From */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">
              Date From
            </label>
            <input
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Date To */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Date To</label>
            <input
              type="datetime-local"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl">
          Failed to load invoices: {error?.message || "Unknown error"}
        </div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500 text-lg">
          Loading invoices...
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm text-center text-gray-500 border">
          No invoices match the current filters.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Reference
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Patient
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Hospital No.
                  </th>
                  <th className="text-right py-4 px-5 font-medium text-gray-600">
                    Amount
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Method
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Channel
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Status
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Date & Time
                  </th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.map((inv) => {
                  const createdDate = new Date(
                    inv.created_at
                  ).toLocaleDateString();
                  const createdTime = new Date(
                    inv.created_at
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  return (
                    <tr
                      key={inv.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3.5 px-5 font-medium text-gray-700">
                        {inv.reference}
                      </td>
                      <td className="py-3.5 px-5">
                        {inv.customer_meta?.patient_first_name}{" "}
                        {inv.customer_meta?.patient_last_name || "—"}
                      </td>
                      <td className="py-3.5 px-5 text-gray-500">
                        {inv.customer_meta?.patient_hospital_number || "—"}
                      </td>
                      <td className={`py-3.5 px-5 text-right ${amountColor(inv.status)}`}>
                        ₦{parseFloat(inv.invoice_amount || inv.amount || 0).toLocaleString()}
                      </td>
                      <td className="py-3.5 px-5 text-gray-600">
                        {inv.payment_method?.toLowerCase() === "wallet" ? "Wallet" : "QuickPay"}
                      </td>
                      <td className="py-3.5 px-5 text-gray-600">
                        {inv.payment_method?.toLowerCase() === "remita" ? "Remita" : inv.payment_method ? "Monnify" : "—"}
                      </td>
                      <td className="py-3.5 px-5">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusBadge(
                            inv.status
                          )}`}
                        >
                          {inv.status?.toUpperCase() || "UNKNOWN"}
                        </span>
                      </td>
                      <td className="py-3.5 px-5 text-gray-500 whitespace-nowrap">
                        <div>{createdDate}</div>
                        <div className="text-xs text-gray-400">{createdTime}</div>
                      </td>
                      <td className="py-3.5 px-5">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedInvoice(inv)}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3.5 bg-gray-50 border-t text-sm text-gray-500">
            Showing <strong>{filteredInvoices.length}</strong> of{" "}
            <strong>{allInvoices.length}</strong> invoices
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      <Dialog
        open={!!selectedInvoice}
        onOpenChange={() => setSelectedInvoice(null)}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
            <DialogDescription>
              Reference: {selectedInvoice?.reference}
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="py-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Patient Name</p>
                  <p className="font-medium">
                    {selectedInvoice.customer_meta?.patient_first_name}{" "}
                    {selectedInvoice.customer_meta?.patient_last_name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Hospital No.</p>
                  <p className="font-medium">
                    {selectedInvoice.customer_meta?.patient_hospital_number || "—"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Patient Email</p>
                  <p className="font-medium">{selectedInvoice.customer_meta?.patient_email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Patient Phone</p>
                  <p className="font-medium">{selectedInvoice.customer_meta?.patient_phone_number || "—"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Payer Name</p>
                  <p className="font-medium">
                    {selectedInvoice.customer_meta?.payer_first_name}{" "}
                    {selectedInvoice.customer_meta?.payer_last_name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Payer Email</p>
                  <p className="font-medium">{selectedInvoice.customer_meta?.payer_email || "—"}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-0.5">Payer Phone</p>
                <p className="font-medium">{selectedInvoice.customer_meta?.payer_phone_number || "—"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-0.5">Description</p>
                <p>
                  {selectedInvoice.customer_meta?.transaction_description || "—"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Subtotal</p>
                  <p className="text-xl font-bold">
                    ₦{parseFloat(selectedInvoice.invoice_amount || selectedInvoice.total_amount || selectedInvoice.amount || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Service Charge</p>
                  <p className="text-xl font-bold text-blue-600">
                    ₦{parseFloat(selectedInvoice.charges || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Method</p>
                  <p className="font-medium">
                    {selectedInvoice.payment_method?.toLowerCase() === "wallet" ? "Wallet" : "QuickPay"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Channel</p>
                  <p className="font-medium">
                    {selectedInvoice.payment_method?.toLowerCase() === "remita" ? "Remita" : selectedInvoice.payment_method ? "Monnify" : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Status</p>
                  <span
                    className={`px-2.5 py-1 text-xs rounded-full font-semibold ${statusBadge(
                      selectedInvoice.status
                    )}`}
                  >
                    {selectedInvoice.status?.toUpperCase() || "UNKNOWN"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Created</p>
                  <p>
                    {new Date(selectedInvoice.created_at).toLocaleDateString()}{" "}
                    •{" "}
                    {new Date(selectedInvoice.created_at).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit" }
                    )}
                  </p>
                </div>
              </div>

              {selectedInvoice.items?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-1.5 font-medium">
                    Services / Items
                  </p>
                  <ul className="border rounded-lg divide-y text-sm bg-gray-50">
                    {selectedInvoice.items.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex justify-between px-3 py-2"
                      >
                        <span>{item.service?.name || "Unknown service"}</span>
                        <span className="font-semibold">
                          ₦{parseFloat(item.amount || 0).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedInvoice(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={!!editInvoice} onOpenChange={() => setEditInvoice(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Invoice Status</DialogTitle>
            <DialogDescription>
              Ref: {editInvoice?.reference}
            </DialogDescription>
          </DialogHeader>
          {editInvoice && (
            <div className="py-4 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Patient</p>
                <p className="font-medium">
                  {editInvoice.customer_meta?.patient_first_name}{" "}
                  {editInvoice.customer_meta?.patient_last_name || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Amount</p>
                <p className="text-xl font-bold">
                  ₦{parseFloat(editInvoice.amount || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">
                  Current Status:{" "}
                  <strong className="uppercase">{editInvoice.status}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Status
                </label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInvoice(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveStatus}
              disabled={
                updateStatusMutation.isLoading ||
                !newStatus ||
                newStatus === editInvoice?.status
              }
            >
              {updateStatusMutation.isLoading ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminInvoices;